import { Check, ArrowRight, Clock, Info, Utensils, PartyPopper } from "lucide-react";
import { arrangementen, combiAvond, voorwaarden, sfeer } from "../../data/content";

const soortVanId = {
  basisfeest: "Basisfeest (verjaardag/jubileum)",
  themafeest: "Themafeest",
  buffet: "Buffetarrangement",
};

const vraagAan = (a) => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
  window.dispatchEvent(
    new CustomEvent("prefill-aanvraag", {
      detail: { soort: soortVanId[a.id] || "Anders", bericht: `Ik heb interesse in het ${a.titel}.` },
    })
  );
};

const ArrangementCard = ({ a }) => (
  <article
    data-testid={`arrangement-card-${a.id}`}
    className="bg-stone-900/70 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 shadow-2xl hover:-translate-y-1 transition-transform duration-300 flex flex-col"
  >
    <div className="aspect-video overflow-hidden">
      <img src={a.afbeelding} alt={a.titel} className="w-full h-full object-cover" />
    </div>
    <div className="p-6 flex flex-col flex-1">
      <span className="inline-flex self-start items-center bg-secondary/15 text-secondary text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
        {a.geschiktVoor}
      </span>
      <h3 className="font-heading text-2xl font-semibold">{a.titel}</h3>
      <p className="text-sm font-medium text-primary mt-1">{a.subtitel}</p>
      <p className="mt-3 text-muted-foreground leading-relaxed">{a.omschrijving}</p>

      {a.inbegrepen && (
        <ul className="mt-5 space-y-2">
          {a.inbegrepen.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-foreground/85">
              <Check size={16} className="text-primary mt-0.5 shrink-0" /> {item}
            </li>
          ))}
        </ul>
      )}

      {a.themas && (
        <div className="mt-5">
          <p className="text-sm font-medium text-foreground mb-2">Beschikbare thema's</p>
          <div className="flex flex-wrap gap-2">
            {a.themas.map((t) => (
              <span key={t} className="inline-flex items-center bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background/60 text-muted-foreground">
              <th className="text-left font-medium px-4 py-2">Aantal</th>
              <th className="text-right font-medium px-4 py-2">Prijs p.p.</th>
            </tr>
          </thead>
          <tbody>
            {a.prijzen.map((p, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-4 py-2">{p.label}</td>
                <td className="px-4 py-2 text-right">{p.pp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Clock size={14} /> {a.meta}
      </p>

      <button
        data-testid={`arrangement-info-${a.id}`}
        onClick={() => vraagAan(a)}
        className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground font-medium rounded-full px-5 py-3 hover:bg-primary-hover transition-colors"
      >
        Vraag informatie aan <ArrowRight size={16} />
      </button>
    </div>
  </article>
);

export const Arrangementen = () => {
  return (
    <section id="arrangementen" data-testid="arrangementen-section" className="relative py-16 md:py-28 overflow-hidden">
      <div className="absolute inset-0">
        <img src={sfeer.bar} alt="" aria-hidden="true" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/85 backdrop-blur-[2px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <span className="text-primary font-semibold text-sm uppercase tracking-wide">Arrangementen 2026</span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mt-3">
            Bekijk de mogelijkheden voor jouw groep
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            We werken met drie soorten arrangementen, afhankelijk van de gelegenheid, de groepsgrootte en de gewenste sfeer. Alle prijzen zijn indicatief en inclusief btw.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {arrangementen.map((a) => (
            <ArrangementCard key={a.id} a={a} />
          ))}
        </div>

        {/* Eten én feesten op één avond */}
        <div data-testid="combi-avond" className="mt-12 grid lg:grid-cols-2 gap-8 items-center bg-stone-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-7 md:p-10">
          <div>
            <span className="text-primary font-semibold text-sm uppercase tracking-wide">{combiAvond.subtitel}</span>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold mt-2">{combiAvond.titel}</h3>
            <p className="mt-4 text-muted-foreground leading-relaxed">{combiAvond.tekst}</p>
            <p className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
              <Info size={15} className="text-primary mt-0.5 shrink-0" /> {combiAvond.noot}
            </p>
          </div>
          <div className="space-y-4">
            {combiAvond.stappen.map((s, i) => {
              const Icon = i === 0 ? Utensils : PartyPopper;
              return (
                <div key={i} className="flex items-start gap-4 bg-background/60 border border-border rounded-xl p-5">
                  <span className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-primary" />
                  </span>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-secondary">{s.tijd}</span>
                    <h4 className="font-heading text-lg font-semibold leading-tight">{s.titel}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{s.tekst}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Voorwaarden */}
        <div data-testid="voorwaarden" className="mt-8 bg-stone-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-7 md:p-10">
          <h3 className="font-heading text-xl md:text-2xl font-semibold">Boeken & voorwaarden</h3>
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {voorwaarden.map((v, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground/85 leading-relaxed">
                <Check size={16} className="text-primary mt-0.5 shrink-0" /> {v}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
